/**
 * Icon fallbacks for connectors whose catalogue row carries no `iconUrl`.
 *
 * Ported from Amoreg's `lib/composio/connector-icons` so the Plugins page in
 * this workspace shows the same marks as the product it mirrors. Keyed on the
 * toolkit slug first and the connector id second, because the slug is the
 * provider's own name for the integration and the id is ours.
 *
 * Shared by both catalogues — the personal Plugins page and the workspace
 * Integrations page — so a connector without an `iconUrl` never renders as a
 * generic plug on one surface and a real mark on the other.
 *
 * @module lib/connectorIcons
 */

const CONNECTOR_ICON_FALLBACKS: Record<string, string> = {
  airtable: "https://cdn.simpleicons.org/airtable",
  // Official site icon, bundled because Attio is absent from Simple Icons.
  attio: "/integrations/attio.ico",
  asana: "https://cdn.simpleicons.org/asana",
  box: "https://cdn.simpleicons.org/box",
  clickup: "https://cdn.simpleicons.org/clickup",
  confluence: "https://cdn.simpleicons.org/confluence",
  dropbox: "https://cdn.simpleicons.org/dropbox",
  github: "https://cdn.simpleicons.org/github",
  gmail: "https://cdn.simpleicons.org/gmail",
  googlecalendar: "https://cdn.simpleicons.org/googlecalendar",
  googledocs: "https://cdn.simpleicons.org/googledocs",
  googledrive:
    "https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg",
  googlesheets: "https://cdn.simpleicons.org/googlesheets",
  googleslides: "https://cdn.simpleicons.org/googleslides",
  hubspot: "https://cdn.simpleicons.org/hubspot",
  intercom: "https://cdn.simpleicons.org/intercom",
  jira: "https://cdn.simpleicons.org/jira",
  linear: "https://cdn.simpleicons.org/linear",
  microsoft_teams:
    "https://upload.wikimedia.org/wikipedia/commons/9/94/Microsoft_Office_Teams_%282019%E2%80%932025%29.svg",
  monday: "https://upload.wikimedia.org/wikipedia/commons/c/c6/Monday_logo.svg",
  notion: "https://cdn.simpleicons.org/notion",
  // Vendored rather than hotlinked: PlainSail, Laserfiche, RuleGuard and
  // Altruist are partners, not CDN-hosted public brands, so the marks ship
  // with the app and cannot break on us. Altruist's is its own square mark
  // (the lowercase "a"), taken from altruist.com's 192px site icon.
  altruist: "/integrations/altruist.png",
  // A monogram in iManage's navy: the catalogue's own icon renders blank.
  imanage: "/integrations/imanage.svg",
  laserfiche: "/integrations/laserfiche.svg",
  plainsail: "/integrations/plainsail.svg",
  ruleguard: "/integrations/ruleguard.png",
  onedrive:
    "https://upload.wikimedia.org/wikipedia/commons/5/59/Microsoft_Office_OneDrive_%282019%E2%80%932025%29.svg",
  outlook:
    "https://upload.wikimedia.org/wikipedia/commons/4/45/Microsoft_Office_Outlook_%282018%E2%80%932024%29.svg",
  salesforce: "https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg",
  sharepoint:
    "https://upload.wikimedia.org/wikipedia/commons/e/ec/Microsoft_Office_SharePoint_%282019%E2%80%932025%29.svg",
  slack: "https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg",
  trello: "https://cdn.simpleicons.org/trello",
  zendesk: "https://cdn.simpleicons.org/zendesk",
};

const CONNECTOR_ICON_SLUG_ALIASES: Record<string, string> = {
  google_calendar: "googlecalendar",
  google_docs: "googledocs",
  google_sheets: "googlesheets",
  google_slides: "googleslides",
  microsoftteams: "microsoft_teams",
  monday_com: "monday",
  mondaycrm: "monday",
  one_drive: "onedrive",
  share_point: "sharepoint",
};

function normalizeConnectorIconKey(value: string | null | undefined): string | null {
  const normalized = value
    ?.toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-]+/g, "_");
  return normalized || null;
}

// Vendored marks that are already a filled square tile. They fill their icon
// box edge to edge; a frame and padding around them read as a tile in a tile.
const FULL_TILE_ICON_URLS: ReadonlySet<string> = new Set([
  "/integrations/imanage.svg",
  "/integrations/plainsail.svg",
]);

/** True when the mark is its own tile, so it must not get a frame or padding. */
export function isFullTileIcon(url: string | null | undefined): boolean {
  return url != null && FULL_TILE_ICON_URLS.has(url);
}

/** The icon URL to fall back to, or null when we have no mark for this one. */
export function resolveConnectorIconFallback(input: {
  readonly id?: string | null;
  readonly toolkitSlug?: string | null;
}): string | null {
  const keys = [
    normalizeConnectorIconKey(input.toolkitSlug),
    normalizeConnectorIconKey(input.id),
  ].filter((key): key is string => Boolean(key));

  for (const key of keys) {
    const resolvedKey = CONNECTOR_ICON_SLUG_ALIASES[key] ?? key;
    const fallback = CONNECTOR_ICON_FALLBACKS[resolvedKey] ?? CONNECTOR_ICON_FALLBACKS[key];
    if (fallback) {
      return fallback;
    }
  }

  return null;
}
